import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { keys } from '../api/hooks';
import { Button, Field, Input } from '../components/ui';

export function LoginPage() {
  const client = useQueryClient();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/login', { username, password });
      // Refetching rather than writing the response into the cache: /me also
      // carries the shared-with-me count, and one round trip keeps the two
      // from disagreeing.
      await client.invalidateQueries({ queryKey: keys.me });
    } catch (caught) {
      setError((caught as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-dvh place-items-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-2.5">
          <span className="grid size-9 place-items-center rounded-xl bg-brand text-lg font-semibold text-brand-ink">
            W
          </span>
          <h1 className="text-xl font-semibold">Wood Family Files</h1>
        </div>

        <form
          onSubmit={submit}
          className="rounded-2xl border border-line bg-surface p-5 shadow-sm"
        >
          <Field label="Username">
            <Input
              autoFocus
              autoComplete="username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              required
            />
          </Field>
          <Field label="Password">
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </Field>

          {error ? (
            <p
              role="alert"
              className="mb-4 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
            >
              {error}
            </p>
          ) : null}

          <Button type="submit" variant="primary" loading={busy} className="w-full">
            Sign in
          </Button>
        </form>

        <p className="mt-4 text-center text-xs text-ink-dim">
          Accounts are created by an admin. There is no sign-up.
        </p>
      </div>
    </div>
  );
}
